import styled from "styled-components/macro";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BoxFooter,
  BoxHeader,
  BoxNavLeft,
  BoxTitle,
  BoxContent,
  Box,
  BoxNavRight,
} from "./Box";
import { TrashCan } from "./Icons";
import { touchSkipioApi, useSkipioApi } from "../api";
import useLoadAll from "../hooks/use-load-all";
import { useToast } from "../hooks/use-toast";
import Stack from "./Stack";
import { Label } from "./Input";
import useMoment from "../hooks/use-moment";
import TextareaAutosize from "react-autosize-textarea";
import { BORDER_RADIUS, THEME_COLOR, ERROR_COLOR } from "../vars";
import { useFields } from "../hooks/use-fields";
import Button, { IconButton } from "./Button";
import BoxTimeline, { BoxTimelinePoint } from "./BoxTimeline";
import Title from "./Title";
import Overlay, { OverlayTrigger } from "./Overlay";
import { fetchUser } from "../api/users";

type TContactNote = {
  id: string;
  createdAt: string;
  body: string;
  user: {
    deletedAt: string;
    email: string;
    firstName: string;
    lastName: string;
  };
  userId: string;
};

function ContactNotes({ contactId }: { contactId: string }) {
  const skipioApi = useSkipioApi();
  const queryClient = useQueryClient();
  const moment = useMoment();
  const { data: notes = [], loading } = useLoadAll<TContactNote>(
    skipioApi.contacts[contactId].contactNotes
  );
  const user = useQuery({
    queryKey: ["users", "me"],
    queryFn: fetchUser,
    select: (queryData) => queryData.data,
  }).data!;
  const hasMultiUser = user.account.maximumUsers > 1;
  const { field, set } = useFields({ initialValue: { body: "" } });
  const showToast = useToast();

  async function saveNote() {
    const data = {
      contactNote: {
        body: field("body").value,
      },
    };

    await skipioApi.contacts[contactId].contactNotes.post({}, { data });
    await touchSkipioApi("contacts");
    await queryClient.invalidateQueries({ queryKey: ["contacts"] });
    set("body", "");
  }

  async function deleteNote(noteId: string) {
    await skipioApi.contactNotes[noteId].delete();
    await touchSkipioApi("contacts");
    await queryClient.invalidateQueries({ queryKey: ["contacts"] });
    showToast("Note Deleted");
  }

  return (
    <Container>
      <div>
        <Box>
          <BoxContent>
            <Label>New Note</Label>
            <Editor>
              <Textarea
                async
                placeholder="Write a note..."
                onChange={(e) =>
                  set("body", (e.target as HTMLTextAreaElement).value)
                }
                value={field("body").value}
              />
            </Editor>
            <Button
              disabled={!field("body").value}
              onClick={() => saveNote()}
              variant="small"
            >
              Save
            </Button>
          </BoxContent>
        </Box>
      </div>
      {notes.length === 0 && !loading ? (
        <EmptyState>There are no notes</EmptyState>
      ) : (
        <Notes>
          <BoxTimeline>
            <>
              {notes.map((note) => {
                return (
                  <Note
                    allowDeletion={user.accountOwner || note.userId === user.id}
                    key={note.id}
                    note={note}
                    onDelete={deleteNote}
                    showAuthor={hasMultiUser}
                    timestamp={moment(note.createdAt).calendar()}
                  />
                );
              })}
            </>
          </BoxTimeline>
        </Notes>
      )}
    </Container>
  );
}

function Note({
  allowDeletion,
  note,
  onDelete,
  showAuthor,
  timestamp,
}: {
  allowDeletion: boolean;
  note: TContactNote;
  onDelete: (noteId: string) => void;
  showAuthor: boolean;
  timestamp: string;
}) {
  const author = note.user;

  return (
    <BoxTimelinePoint>
      <Box elevated>
        <Title>
          <BoxContent>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div style={{ paddingRight: 10 }}>
                {note.body}
                <small>
                  {timestamp}
                  {showAuthor && (
                    <>
                      {" by"}
                      <span
                        style={
                          author.deletedAt ? { opacity: 0.5 } : { opacity: 1 }
                        }
                      >
                        {author
                          ? ` ${author.firstName} ${author.lastName} ${
                              !!author.deletedAt ? "(deleted)" : ""
                            }`
                          : " Unknown User"}
                      </span>
                    </>
                  )}
                </small>
              </div>
              {allowDeletion && (
                <DeleteConfirmationOverlay
                  trigger={({ ref, show }) => (
                    <IconButton onClick={show} ref={ref}>
                      <TrashCan />
                    </IconButton>
                  )}
                  deleteNote={() => onDelete(note.id)}
                />
              )}
            </div>
          </BoxContent>
        </Title>
      </Box>
    </BoxTimelinePoint>
  );
}

function DeleteConfirmationOverlay({
  trigger,
  deleteNote,
}: {
  trigger: OverlayTrigger;
  deleteNote: () => void;
}) {
  return (
    <Overlay
      trigger={trigger}
      content={({ hide }) => (
        <Box elevated>
          <BoxHeader white stacked>
            <BoxTitle>Confirm delete?</BoxTitle>
          </BoxHeader>
          <BoxContent>
            Are you sure you want to delete this note? This action cannot be
            undone.
          </BoxContent>
          <BoxFooter>
            <Stack horizontal>
              <BoxNavLeft>
                <Button variant="link" onClick={hide}>
                  Cancel
                </Button>
              </BoxNavLeft>
              <BoxNavRight>
                <Button
                  onClick={deleteNote}
                  style={{ backgroundColor: ERROR_COLOR }}
                >
                  Delete
                </Button>
              </BoxNavRight>
            </Stack>
          </BoxFooter>
        </Box>
      )}
    />
  );
}

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const Notes = styled.div`
  flex: 1;
  margin: auto;
  max-width: 600px;
  margin-top: 20px;
  width: 100%;
`;

const EmptyState = styled.div`
  flex: 1;
  color: #676767;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Textarea = styled(TextareaAutosize)`
  width: 100%;
  min-height: 80px;
  max-height: 200px;
  border: 0;
  resize: none;
  padding: 15px;
  font-size: 14px;
  background: transparent;
  line-height: 1.8em;
`;

const Editor = styled.div`
  border-radius: ${BORDER_RADIUS};
  overflow: hidden;
  border: 0.5px solid #dcdcdc;
  background: #fafafa;
  margin-bottom: 10px;
  &:focus-within {
    border-color: ${THEME_COLOR};
    box-shadow: 0px 0px 0px 2px ${THEME_COLOR}33;
    background-color: #fff;
  }
`;

export default ContactNotes;
